import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
} from '@mui/material'
import VideoCall from '@mui/icons-material/VideoCall'
import Person from '@mui/icons-material/Person'
import Edit from '@mui/icons-material/Edit'
import Cancel from '@mui/icons-material/Cancel'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Upload from '@mui/icons-material/Upload'
import Download from '@mui/icons-material/Download'
import Send from '@mui/icons-material/Send'
import Assessment from '@mui/icons-material/Assessment'
import History from '@mui/icons-material/History'
import Visibility from '@mui/icons-material/Visibility'
import Videocam from '@mui/icons-material/Videocam'
import VideocamOff from '@mui/icons-material/VideocamOff'
import Refresh from '@mui/icons-material/Refresh'
import Warning from '@mui/icons-material/Warning'
import Schedule from '@mui/icons-material/Schedule'
import LocalHospital from '@mui/icons-material/LocalHospital'
import Description from '@mui/icons-material/Description'
import { toast } from 'react-toastify'
import { forwardRef, useImperativeHandle, useState } from 'react'
import { putCPDataWithoutParams } from 'src/clinic/services'
import { putSPDataWithParams } from 'src/supportPortal/services/services'
import {
  APP_CONSTANTS,
  BOOKED_EVENT_STEP_STATUSES,
  BOOKING_PAYMENT_STATUSES,
  BOOKING_SESSION_STATUS_MAP,
  BOOKING_STATUSES,
  EVENT_TYPE_CONSULTATION,
  EVENT_TYPE_SCREENING,
  EVENT_TYPE_THERAPY_SESSION,
  SESSION_PLATFORM_ONLINE,
  STATUS_SESSION_BOOKED,
} from 'src/consumer/constants'
import {
  isCommonResponseSuccessful,
  markBookedSessionAsCompletedFunction,
} from 'src/mentor/helpers/utilityFunctions'
import { getAllRolesInBoolean, canSupportManageSessionForEventType } from 'src/mentor/layout/utils'
import { useNavigate } from 'react-router'
import { styled } from '@mui/material/styles'

const MoreOptionsForBookedSlotStrip = forwardRef(
  (
    {
      handleNewTherapyRequest,
      therapyBookingDataForThisEvent,
      bookedSlot,
      setClickedBookedSlotData,
      setEditPatientDialogOpen,
      forMentor,
      setAddVitalsDialogOpen,
      setEditBookingDialogOpen,
      openSendTherapyPopover,
      refreshBookedEvents,
      handleOpenJoiningDetails,
      openUploadPrescriptionImagesDialog,
      openBookedEventLogs,
      openSendAssessmentDialog,
      openPatientBookingDialog,
      handleToggleRecording,
      openEmrImagesDialog,
      showPrescriptionPdfDownloadButtons = false,
      openPrescriptionPdfDownloadDialog,
      forceShowJoiningDetailsActionButton,
      openRescheduleDialog,
      hideRescheduleDialog,
      showViewReportsButtonInActionButtons = false,
      openReportViewDrawer,
    },
    componentRef,
  ) => {
    const [moreOptionsAnchor, setMoreOptionsAnchor] = useState(null)
    const open = Boolean(moreOptionsAnchor)
    const navigate = useNavigate()
    const roles = getAllRolesInBoolean()
    const isRecordingEnabled = bookedSlot?.recordingEnabled
    const isBookedSlotBlockedForPayment =
      bookedSlot?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
      bookedSlot?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING
    const isSupportPortal = typeof window !== 'undefined' &&
      (window.location?.pathname?.includes('/support') ||
        window.location?.pathname?.includes('/support-home'))
    const hasPreScreeningStep = Boolean(
      bookedSlot?.previousSteps?.some(
        (step) => step?.refType === EVENT_TYPE_SCREENING && step?.refId,
      ),
    )
    const canBookMissingScreening =
      !forMentor &&
      bookedSlot?.eventType === EVENT_TYPE_CONSULTATION &&
      !hasPreScreeningStep &&
      (!isSupportPortal || roles.isL3TherapySupport)

    let showJoiningDetails = false
    if (
      handleOpenJoiningDetails &&
      bookedSlot?.meetingSrc === SESSION_PLATFORM_ONLINE &&
      !isBookedSlotBlockedForPayment &&
      bookedSlot?.status === BOOKING_STATUSES.BOOKED &&
      !isSupportPortal
    ) {
      if (forMentor || forceShowJoiningDetailsActionButton) {
        showJoiningDetails = true
      }
    }

    const handleClick = (event) => {
      event.stopPropagation()
      event.preventDefault()
      setMoreOptionsAnchor(event.currentTarget)
    }
    const handleClose = () => {
      setMoreOptionsAnchor(null)
    }

    useImperativeHandle(componentRef, () => ({
      openMenuOnAnchor: (e) => {
        handleClick(e)
      },
    }))

    async function bookScreeningForThisBooking() {
      const screeningBookApiSignature = isSupportPortal
        ? '/api/secure/cc/support/therapist/meeting/screening/book'
        : '/api/secure/meeting/screening/book'
      const response = await putCPDataWithoutParams(screeningBookApiSignature, {
        ...bookedSlot,
        apiParam: 'booked-event-info',
      })
      if (
        response?.status === 200 &&
        isCommonResponseSuccessful(response?.data?.code) &&
        response?.data?.data?.screening
      ) {
        toast.success(
          `Screening booked for ${response?.data?.data?.screening?.userName} with ${response?.data?.data?.screening?.mentorName}`,
        )
        if (response?.data?.data?.originalSession && setClickedBookedSlotData) {
          setClickedBookedSlotData(response.data.data.originalSession)
        }
      } else {
        toast.error(response?.data?.message || 'Error booking screening')
      }
    }

    async function raiseReassignScreeningsRequest() {
      const response = await putCPDataWithoutParams(
        `/api/secure/meeting/screening/auto-reassign-eligible`,
        {
          ...bookedSlot,
          apiParam: 'booked-event-info',
        },
      )
      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        toast.success('Requested')
      } else {
        toast.error('Error')
      }
    }

    // Styled MenuItem for better visual hierarchy
    const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
      padding: '10px 16px',
      borderRadius: '6px',
      margin: '2px 8px',
      '&:hover': {
        background: 'rgba(67, 56, 202, 0.08)',
      },
    }))

    const MenuSectionLabel = styled(Typography)(({ theme }) => ({
      fontSize: '11px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: 'rgba(0, 0, 0, 0.5)',
      padding: '8px 16px 4px',
      marginTop: '4px',
    }))

    return (
      <div>
        <Menu
          id="booked-slot-actions-menu"
          anchorEl={moreOptionsAnchor}
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              minWidth: '240px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              padding: '8px 0',
            },
          }}
          MenuListProps={{
            'aria-labelledby': 'booked-slot-actions-button',
            sx: { padding: 0 },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* Session & Joining Section */}
          {(showJoiningDetails ||
            bookedSlot?.meetingSrc === SESSION_PLATFORM_ONLINE ||
            (getAllRolesInBoolean().isL1TherapySupport ||
              getAllRolesInBoolean().isL2TherapySupport ||
              getAllRolesInBoolean().isL3TherapySupport)) && (
            <>
              <MenuSectionLabel>Session & Joining</MenuSectionLabel>
              {showJoiningDetails && (
                <StyledMenuItem
                  onClick={() => {
                    handleOpenJoiningDetails()
                    handleClose()
                  }}
                >
                  <ListItemIcon>
                    <VideoCall sx={{ fontSize: 20, color: '#4338ca' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Joining Details"
                    primaryTypographyProps={{
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  />
                </StyledMenuItem>
              )}
              {(getAllRolesInBoolean().isL1TherapySupport ||
                getAllRolesInBoolean().isL2TherapySupport ||
                getAllRolesInBoolean().isL3TherapySupport) && (
                <StyledMenuItem
                  onClick={() => {
                    openBookedEventLogs()
                    handleClose()
                  }}
                >
                  <ListItemIcon>
                    <Description sx={{ fontSize: 20, color: 'rgba(0, 0, 0, 0.6)' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Event Logs"
                    primaryTypographyProps={{
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  />
                </StyledMenuItem>
              )}
              <Divider sx={{ margin: '8px 0' }} />
            </>
          )}

          {/* Reports & Documents Section */}
          {(showViewReportsButtonInActionButtons ||
            showPrescriptionPdfDownloadButtons ||
            (forMentor && bookedSlot?.eventType === EVENT_TYPE_CONSULTATION) ||
            (forMentor &&
              (bookedSlot?.eventType === EVENT_TYPE_THERAPY_SESSION ||
                bookedSlot?.eventType === EVENT_TYPE_SCREENING))) && (
            <>
              <MenuSectionLabel>Reports & Documents</MenuSectionLabel>
              {showViewReportsButtonInActionButtons && (
                <StyledMenuItem
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleClose()
                    if (openReportViewDrawer) {
                      setTimeout(() => {
                        openReportViewDrawer(bookedSlot)
                      }, 100)
                    }
                  }}
                >
                  <ListItemIcon>
                    <Visibility sx={{ fontSize: 20, color: '#4338ca' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="View Report"
                    primaryTypographyProps={{
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  />
                </StyledMenuItem>
              )}
              {showPrescriptionPdfDownloadButtons && (
                <StyledMenuItem
                  onClick={() => {
                    openPrescriptionPdfDownloadDialog()
                    handleClose()
                  }}
                >
                  <ListItemIcon>
                    <Download sx={{ fontSize: 20, color: '#10b981' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Download PDF"
                    primaryTypographyProps={{
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  />
                </StyledMenuItem>
              )}
              {forMentor && bookedSlot?.eventType === EVENT_TYPE_CONSULTATION && (
                <StyledMenuItem
                  onClick={() => {
                    openUploadPrescriptionImagesDialog()
                    handleClose()
                  }}
                >
                  <ListItemIcon>
                    <Upload sx={{ fontSize: 20, color: '#f59e0b' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Upload Prescription"
                    primaryTypographyProps={{
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  />
                </StyledMenuItem>
              )}
              {forMentor &&
                typeof openEmrImagesDialog === 'function' &&
                (!isSupportPortal || canSupportManageSessionForEventType(bookedSlot?.eventType)) &&
                (bookedSlot?.eventType === EVENT_TYPE_THERAPY_SESSION ||
                  bookedSlot?.eventType === EVENT_TYPE_SCREENING) && (
                  <StyledMenuItem
                    onClick={() => {
                      openEmrImagesDialog()
                      handleClose()
                    }}
                  >
                    <ListItemIcon>
                      <Upload sx={{ fontSize: 20, color: '#8b5cf6' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        bookedSlot?.eventType === EVENT_TYPE_SCREENING
                          ? 'Upload Screening Report Images'
                          : 'Upload Therapy Report Images'
                      }
                      primaryTypographyProps={{
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    />
                  </StyledMenuItem>
                )}
              <Divider sx={{ margin: '8px 0' }} />
            </>
          )}

          {/* Actions & Referrals Section */}
          {!forMentor || roles.isDoctor ? (
            <>
              <MenuSectionLabel>Actions & Referrals</MenuSectionLabel>
              {canBookMissingScreening && (
                <StyledMenuItem
                  onClick={() => {
                    bookScreeningForThisBooking()
                    handleClose()
                  }}
                >
                  <ListItemIcon>
                    <LocalHospital sx={{ fontSize: 20, color: '#ec4899' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Book Screening"
                    primaryTypographyProps={{
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  />
                </StyledMenuItem>
              )}
              {forMentor && getAllRolesInBoolean().isDoctor && (
                <StyledMenuItem
                  onClick={() => {
                    // Open unified drawer with referral tab
                    if (openSendTherapyPopover) {
                      openSendTherapyPopover()
                    }
                    handleClose()
                  }}
                >
                  <ListItemIcon>
                    <Send sx={{ fontSize: 20, color: '#8b5cf6' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Refer Patient"
                    secondary="Therapy or Assessment"
                    primaryTypographyProps={{
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                    secondaryTypographyProps={{
                      fontSize: '11px',
                    }}
                  />
                </StyledMenuItem>
              )}
              {forMentor && getAllRolesInBoolean().isDoctor && (
                <StyledMenuItem
                  onClick={() => {
                    openPatientBookingDialog()
                    handleClose()
                  }}
                >
                  <ListItemIcon>
                    <History sx={{ fontSize: 20, color: '#10b981' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Treatment History"
                    primaryTypographyProps={{
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  />
                </StyledMenuItem>
              )}
              {!forMentor && (
                <StyledMenuItem
                  onClick={() => {
                    handleNewTherapyRequest(therapyBookingDataForThisEvent, bookedSlot)
                    handleClose()
                  }}
                >
                  <ListItemIcon>
                    <LocalHospital sx={{ fontSize: 20, color: '#ec4899' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Book Therapy"
                    primaryTypographyProps={{
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  />
                </StyledMenuItem>
              )}
              {Boolean(bookedSlot?.previousSteps) &&
                bookedSlot?.previousSteps?.length === 1 &&
                bookedSlot?.previousSteps?.[0]?.status ===
                  BOOKED_EVENT_STEP_STATUSES.HANDLER_NOT_ASSIGNED && (
                  <StyledMenuItem
                    onClick={() => {
                      raiseReassignScreeningsRequest()
                      handleClose()
                    }}
                  >
                    <ListItemIcon>
                      <Refresh sx={{ fontSize: 20, color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Request Reassign"
                      primaryTypographyProps={{
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    />
                  </StyledMenuItem>
                )}
              <Divider sx={{ margin: '8px 0' }} />
            </>
          ) : null}

          {/* Recording Section */}
          {(getAllRolesInBoolean().isL1TherapySupport ||
            getAllRolesInBoolean().isL2TherapySupport ||
            getAllRolesInBoolean().isL3TherapySupport) &&
            bookedSlot?.sessionStatus === BOOKING_SESSION_STATUS_MAP['not-started']?.id && (
              <>
                <MenuSectionLabel>Recording</MenuSectionLabel>
                <StyledMenuItem
                  onClick={() => {
                    handleToggleRecording()
                    handleClose()
                  }}
                >
                  <ListItemIcon>
                    {isRecordingEnabled ? (
                      <VideocamOff sx={{ fontSize: 20, color: '#dc2626' }} />
                    ) : (
                      <Videocam sx={{ fontSize: 20, color: '#10b981' }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={isRecordingEnabled ? 'Disable Recording' : 'Enable Recording'}
                    primaryTypographyProps={{
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  />
                </StyledMenuItem>
                <Divider sx={{ margin: '8px 0' }} />
              </>
            )}

          {/* Booking Management Section */}
          <MenuSectionLabel>Booking Management</MenuSectionLabel>
          {/* Mark Completed - Only show for booked status */}
          {forMentor &&
           !bookedSlot?.markedCompleted &&
           !isBookedSlotBlockedForPayment &&
           (!isSupportPortal || canSupportManageSessionForEventType(bookedSlot?.eventType)) &&
           (bookedSlot?.status === STATUS_SESSION_BOOKED || bookedSlot?.status === BOOKING_STATUSES.BOOKED) && (
            <StyledMenuItem
              onClick={async () => {
                if (isSupportPortal) {
                  // Support users (e.g. Screener Supervisor) can't call the teacher finish endpoint;
                  // use the support endpoint which resolves the practitioner from the booked event.
                  const resp = await putSPDataWithParams(
                    '/api/secure/cc/support/events/booked-event/finish',
                    null,
                    { bookedEventId: bookedSlot?.id },
                  )
                  if (resp?.status === 200 && isCommonResponseSuccessful(resp?.data?.code)) {
                    toast.success('Session marked completed')
                  } else {
                    toast.error(
                      resp?.data?.message ||
                        resp?.response?.data?.message ||
                        'Could not mark the session completed',
                    )
                  }
                } else {
                  await markBookedSessionAsCompletedFunction(bookedSlot)
                }
                refreshBookedEvents()
                handleClose()
              }}
            >
              <ListItemIcon>
                <CheckCircle sx={{ fontSize: 20, color: '#10b981' }} />
              </ListItemIcon>
              <ListItemText
                primary="Mark Completed"
                primaryTypographyProps={{
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              />
            </StyledMenuItem>
          )}
          <StyledMenuItem
            onClick={() => {
              setClickedBookedSlotData(bookedSlot)
              setEditPatientDialogOpen(true)
              handleClose()
            }}
          >
            <ListItemIcon>
              <Person sx={{ fontSize: 20, color: '#6366f1' }} />
            </ListItemIcon>
            <ListItemText
              primary="Patient Details"
              primaryTypographyProps={{
                fontSize: '14px',
                fontWeight: 500,
              }}
            />
          </StyledMenuItem>
          {!isBookedSlotBlockedForPayment && (
            <StyledMenuItem
              onClick={() => {
                setClickedBookedSlotData(bookedSlot)
                setAddVitalsDialogOpen(true)
                handleClose()
              }}
            >
              <ListItemIcon>
                <Description sx={{ fontSize: 20, color: '#8b5cf6' }} />
              </ListItemIcon>
              <ListItemText
                primary="Add Vitals"
                primaryTypographyProps={{
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              />
            </StyledMenuItem>
          )}
          <StyledMenuItem
            onClick={() => {
              setClickedBookedSlotData(bookedSlot)
              setEditBookingDialogOpen(true)
              handleClose()
            }}
          >
            <ListItemIcon>
              <Edit sx={{ fontSize: 20, color: '#4338ca' }} />
            </ListItemIcon>
            <ListItemText
              primary="Edit Booking"
              primaryTypographyProps={{
                fontSize: '14px',
                fontWeight: 500,
              }}
            />
          </StyledMenuItem>
          {openRescheduleDialog &&
            !isBookedSlotBlockedForPayment &&
            !bookedSlot?.markedCompleted &&
            !hideRescheduleDialog && (
              <StyledMenuItem
                onClick={() => {
                  openRescheduleDialog()
                  handleClose()
                }}
              >
                <ListItemIcon>
                  <Schedule sx={{ fontSize: 20, color: '#f59e0b' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Reschedule"
                  primaryTypographyProps={{
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                />
              </StyledMenuItem>
            )}

        </Menu>
      </div>
    )
  },
)

export default MoreOptionsForBookedSlotStrip
